/**
 * Admin → Listings → one listing. Facts, photos, the dossier in review mode
 * (verify / needs more info / reject per item) and the listing-level actions.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, Checkbox, Chip, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, IconButton, ImageList, ImageListItem, LinearProgress, Link, Stack, TextField, Typography } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import adminService from '../services/adminService';
import DossierAccordion from '../components/listings/DossierAccordion';
import VerificationFindingsPanel from '../components/listings/VerificationFindingsPanel';
import { forcePublishLabel, signOffNotice } from '../components/listings/adminListingCopy';
import DocumentCompletenessStrip from '../components/admin/DocumentCompletenessStrip';
import { panelSx, panelHeaderSx } from '../components/admin/surfaces';
import { STATUS_COPY, STATUS_CHIP_COLOR } from '../components/listings/ListingStatusBanner';
import { text } from '../theme/colors';

const DIALOGS = {
  return: { title: 'Return to realtor', body: 'The realtor gets the per-item notes you wrote plus this message.', field: 'Message (optional)', action: 'Return', color: 'warning' },
  reject: { title: 'Reject listing', body: 'Rejection is final. The realtor is told the reason.', field: 'Reason (required)', action: 'Reject', color: 'error' },
  unpublish: { title: 'Hide this listing from buyers?', body: 'It stays verified. The realtor is told why it came down.', field: 'Reason (required)', action: 'Unpublish', color: 'warning' },
  publish: { title: 'Publish this listing?', body: 'Buyers will see the property and its Eqaya Verified badge.', action: 'Publish', color: 'primary' },
};

const Fact = ({ label, value }) => (
  <Grid item xs={6} md={3}><Typography variant="caption" sx={{ color: text.secondary }}>{label}</Typography><Typography sx={{ fontWeight: 500 }}>{value || '—'}</Typography></Grid>
);

export default function AdminListingReviewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  // A reload after an action keeps the page on screen — only the first load
  // is allowed to blank it out and show the spinner.
  const [refreshing, setRefreshing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [dialog, setDialog] = useState(null); // 'return' | 'reject' | 'unpublish' | 'publish'
  const [notes, setNotes] = useState('');
  // Force only exists to override a hide Eqaya itself put on the listing.
  const [force, setForce] = useState(false);

  // A load in flight when the admin moves to another listing must not write
  // the old row (or the old error) into this page — same guard as the public
  // listing pages.
  const live = useRef(true);
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
  const load = useCallback(async (quiet = false) => {
    if (quiet) setRefreshing(true); else setLoading(true);
    try { const l = await adminService.getListing(id); if (live.current) setListing(l); }
    catch (e) { if (live.current) setError(e.response?.data?.error || 'Failed to load listing'); }
    finally { if (live.current) { setLoading(false); setRefreshing(false); } }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const fail = (e, fallback) => {
    const d = e.response?.data;
    setNotice('');
    if (d?.errorCode === 'DOSSIER_NOT_REVIEWED') setError(`Review these items first: ${(d.pending || []).join(', ')}`);
    // The server answers in flag keys; the admin only ever saw the labels.
    else if (d?.errorCode === 'FLAGS_OPEN') {
      const flags = listing?.verification?.flags || [];
      const label = (k) => flags.find((f) => f.key === k)?.label || k;
      setError(`Dismiss these discrepancies first: ${(d.open || []).map(label).join(', ')}`);
    // PUBLISH_BLOCKED already carries the reason in prose — say exactly that.
    } else setError(d?.error || fallback);
  };
  const review = async (key, decision, itemNotes) => {
    try { setError(''); await adminService.reviewListingItem(id, key, decision, itemNotes); setNotice(`${key}: ${decision.replace('_', ' ')}`); await load(true); } catch (e) { fail(e, 'Review failed'); }
  };
  const approve = async () => {
    setBusy(true);
    try { setError(''); await adminService.deskApproveListing(id); setNotice('Dossier approved — realtor notified'); await load(true); }
    catch (e) { fail(e, 'Approve failed'); } finally { setBusy(false); }
  };
  const act = async () => {
    const which = dialog; setDialog(null); setBusy(true);
    try {
      setError('');
      if (which === 'return') await adminService.returnListing(id, notes);
      else if (which === 'unpublish') await adminService.unpublishListing(id, notes);
      else if (which === 'publish') await adminService.publishListing(id, force);
      else if (which === 'reject') await adminService.rejectListing(id, notes);
      else {
        // A dialog key with no branch would otherwise silently reject a listing.
        console.error('Unknown listing dialog action', which);
        return;
      }
      setNotice({ return: 'Returned to realtor', reject: 'Listing rejected', unpublish: 'Listing hidden from buyers', publish: 'Listing published' }[which]);
      setNotes(''); await load(true);
    } catch (e) { fail(e, 'Action failed'); } finally { setBusy(false); }
  };
  // A stale draft from a dialog the admin cancelled must not ride along into the next one.
  const openDialog = (which) => { setNotes(''); setForce(false); setDialog(which); };

  const markVerified = async (dismissals) => {
    setBusy(true);
    // Sign-off either publishes, parks or hides the listing — the server's
    // answer says which, so the notice reads it rather than guessing.
    try { setError(''); const updated = await adminService.markListingVerified(id, dismissals); setNotice(signOffNotice(updated)); await load(true); }
    catch (e) { fail(e, 'Could not mark verified'); } finally { setBusy(false); }
  };
  const failVerification = async (failNotes) => {
    setBusy(true);
    try { setError(''); await adminService.failListingVerification(id, failNotes); setNotice('Verification failed — realtor notified'); await load(true); }
    catch (e) { fail(e, 'Could not fail the verification'); } finally { setBusy(false); }
  };

  if (loading) return <Container sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Container>;
  if (!listing) return <Container sx={{ py: 4 }}><Alert severity="error">{error || 'Listing not found'}</Alert></Container>;
  // The payload is the server's, but a dossier or photo array that failed to
  // come back must cost a section, not the whole review page.
  const d = { ...(listing.dossier || {}), items: listing.dossier?.items || [] };
  const reviewing = listing.status === 'submitted';
  // A renewed mandate on a verified or live listing is the one item checked outside desk review.
  const mandateReviewable = ['verified', 'published', 'unpublished'].includes(listing.status);
  const requiredItems = d.items.filter((i) => i.required);
  // An N/A claim the admin has not decided still counts as pending on the
  // backend, so mirror that rule here rather than trusting `complete`.
  const naUndecided = requiredItems.filter((i) => i.rawStatus === 'not_applicable' && !i.reviewedBy);
  const canApprove = reviewing && requiredItems.every((i) => i.status === 'approved' && !(i.rawStatus === 'not_applicable' && !i.reviewedBy));
  const canReturn = reviewing && requiredItems.some((i) => ['flagged', 'rejected'].includes(i.status));
  const realtorName = [listing.realtor?.display_name, listing.realtor?.agency_name].filter(Boolean).join(' · ') || listing.realtor_user_id;

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {refreshing && <LinearProgress sx={{ mb: 1 }} />}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate('/admin/home/listings')}><ArrowBack /></IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>{listing.title}</Typography>
          <Typography variant="body2" sx={{ color: text.secondary, textTransform: 'capitalize' }}>
            {listing.listing_type} · <Link component={RouterLink} to={`/admin/home/users/${listing.realtor_user_id}`} underline="hover" sx={{ textTransform: 'none' }}>{realtorName}</Link>
          </Typography>
        </Box>
        <Chip color={STATUS_CHIP_COLOR[listing.status] || 'default'} label={(STATUS_COPY[listing.status] || {}).title || listing.status} />
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}
      {listing.desk_review_notes && <Alert severity="info" sx={{ mb: 2 }}>Last desk note: {listing.desk_review_notes}</Alert>}

      <Box sx={{ ...panelSx, mb: 3 }}>
        <Box sx={panelHeaderSx}><Typography variant="subtitle2">Listing facts</Typography></Box>
        <Grid container spacing={2} sx={{ p: 2 }}>
          <Fact label="Price" value={listing.price_usd ? `USD ${Number(listing.price_usd).toLocaleString()}` : null} />
          <Fact label="Size" value={listing.stand_size_sqm ? `${listing.stand_size_sqm} m²` : null} />
          <Fact label="Deed number" value={listing.deed_number} />
          <Fact label="Diagram number" value={listing.diagram_number} />
          <Fact label="Rates account" value={listing.rates_account_number} />
          <Fact label="Seller on deed" value={listing.seller_name_on_deed} />
          <Fact label="SI 76 validated" value={listing.deed_validated_si76 ? 'Yes' : 'No'} />
          <Fact label="Location" value={[listing.location?.address, listing.location?.suburb, listing.location?.city].filter(Boolean).join(', ')} />
          <Fact label="Conveyancer" value={[listing.conveyancer?.firm, listing.conveyancer?.contact_name, listing.conveyancer?.phone].filter(Boolean).join(' · ')} />
          <Grid item xs={12}><Typography variant="caption" sx={{ color: text.secondary }}>Description</Typography><Typography>{listing.description || '—'}</Typography></Grid>
        </Grid>
        {(listing.photos || []).length > 0 && (
          <ImageList cols={2} gap={6} sx={{ px: 2, pb: 2, gridTemplateColumns: { xs: 'repeat(2, 1fr) !important', sm: 'repeat(6, 1fr) !important' } }}>
            {(listing.photos || []).map((p) => (
              <ImageListItem key={p.url}>
                {/* Full size in a new tab: a 6-across thumbnail is useless for reading a boundary. */}
                <a href={p.url} target="_blank" rel="noopener noreferrer"><img src={p.url} alt={listing.title} loading="lazy" style={{ borderRadius: 6, width: '100%' }} /></a>
              </ImageListItem>
            ))}
          </ImageList>
        )}
      </Box>

      {/* Keyed on the request: a re-verification is a fresh set of flags, and
          the panel's typed notes must not survive into it. */}
      {listing.verification && (
        <VerificationFindingsPanel key={listing.verification.requestId} listing={listing} onMarkVerified={markVerified} onFail={failVerification} busy={busy} />
      )}

      <Box sx={{ ...panelSx, mb: 3 }}>
        <Box sx={{ ...panelHeaderSx, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="subtitle2" sx={{ flex: 1 }}>Due-diligence dossier</Typography>
          {/* The counts are over required items, so the chips must be too. */}
          <DocumentCompletenessStrip summary={{ ...d, items: requiredItems }} noun="Items" />
        </Box>
        <Box sx={{ p: 2 }}>
          {naUndecided.length > 0 && reviewing && (
            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: text.secondary }}>N/A claims need your Verify (accept) or Reject.</Typography>
          )}
          {!reviewing && <Alert severity="info" sx={{ mb: 2 }}>Items can only be reviewed while the listing is submitted{mandateReviewable ? ', except a renewed mandate to sell' : ''}.</Alert>}
          <DossierAccordion listing={listing} reviewMode canDecide={reviewing} decidableKeys={mandateReviewable ? ['mandate_to_sell'] : []} onReview={review} />
        </Box>
      </Box>

      <Stack direction="row" spacing={1} justifyContent="flex-end">
        {listing.status === 'published' && <Button variant="outlined" color="warning" disabled={busy} onClick={() => openDialog('unpublish')}>Unpublish</Button>}
        {['verified', 'unpublished'].includes(listing.status) && <Button variant="contained" disabled={busy} onClick={() => openDialog('publish')}>Publish</Button>}
        <Button variant="outlined" color="error" disabled={busy || !['submitted', 'changes_requested', 'desk_approved'].includes(listing.status)} onClick={() => openDialog('reject')}>Reject listing</Button>
        <Button variant="outlined" color="warning" disabled={busy || !canReturn} onClick={() => openDialog('return')}>Return to realtor</Button>
        <Button variant="contained" color="success" disabled={busy || !canApprove} onClick={approve}>Approve dossier</Button>
      </Stack>

      <Dialog open={!!dialog} onClose={() => setDialog(null)} fullWidth maxWidth="sm" aria-labelledby="listing-action-title">
        <DialogTitle id="listing-action-title">{DIALOGS[dialog]?.title || 'Reject listing'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: text.secondary }}>{DIALOGS[dialog]?.body}</Typography>
          {dialog === 'publish' ? (
            // Always offered: the server also demands force when the agency's
            // EAC standing is bad, and that standing is not in this payload.
            <FormControlLabel
              control={<Checkbox checked={force} onChange={(e) => setForce(e.target.checked)} />}
              label={forcePublishLabel(listing)}
            />
          ) : (
            <TextField fullWidth multiline minRows={3} label={DIALOGS[dialog]?.field} value={notes} onChange={(e) => setNotes(e.target.value)} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          {/* The realtor is told why their listing came down, so "why" is not optional. */}
          <Button variant="contained" color={DIALOGS[dialog]?.color} disabled={busy || (['reject', 'unpublish'].includes(dialog) && notes.trim().length < 3)} onClick={act}>{DIALOGS[dialog]?.action || 'Reject'}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
